import { configuration } from '../configuration'
import { helpers, type PluginConfiguration } from '../helpers'
import { icons } from '../icons'
import { syntaxHighlighter } from '../syntaxHighlighter'

export const requests: PluginConfiguration = {
  name: 'Requests',
  id: 'requests',
  icon: icons.requests,
  // visit https://play.tailwindcss.com to view generated table
  extra: `<div class="cursor-pointer" title="Copy for administration" onclick="navigator.clipboard.writeText('document.querySelector(\\'[data-fu-tools-table-content=requests]\\').innerHTML = \`' + document.querySelector('[data-fu-tools-table-content=requests]').innerHTML.replaceAll('<!--import-->', '<div class=\\'inline-block w-4 h-4 text-[#364153] rounded-full mr-2 uppercase text-xs/[17px] text-center bg-[#ffd7a8]\\' title=\\'Imported\\'>I</div>')
  .replaceAll('\\'', '\\\\\\'')
  // magic for replacing quotes for fetch
  .replace(new RegExp(atob('KGJvZHkmcXVvdDs6cyomcXVvdDspKC4qPykoPz0mcXVvdDssJnF1b3Q7bWV0aG9kJnF1b3Q7OiZxdW90O1BPU1Qp')), (match, p1, p2) => p1 + p2.replaceAll(atob('XCZxdW90Ow=='), atob('XFwmcXVvdDs='))) + '\`')"><svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="h-3.5 w-3.5 text-[#99a1af] hover:text-[#00d3f3] duration-200 transition-all">${icons.tableCopy}</svg></div>

  <div class="cursor-pointer inline-flex items-center" title="Copy as HTML" onclick="navigator.clipboard.writeText('<div class=\\'bg-[#1e2939]\\'>' + document.querySelector('[data-fu-tools-table-content=requests]').innerHTML.replaceAll('<!--import-->', '<div class=\\'inline-block w-4 h-4 text-[#364153] rounded-full mr-2 uppercase text-xs/[17px] text-center bg-[#ffd7a8]\\' title=\\'Imported\\'>I</div>') + '</div><dialog id=\\'fu-tools-dialog\\' class=\\'top-0 left-0 m-0 p-0 font-mono z-9999999\\'><div class=\\'fixed inset-0 bg-[#101828] opacity-50\\'></div><div class=\\'fixed inset-0 flex items-center justify-center\\'><div class=\\'text-white bg-[#1e2939] rounded-lg p-4 max-w-9/10 w-full max-h-9/10 flex flex-col\\'><div class=\\'outline-0 flex-1 overflow-auto mt-1 break-words whitespace-break-spaces\\' id=\\'fu-tools-dialog-content\\'></div><form method=\\'dialog\\' class=\\'flex justify-end\\'><button class=\\'bg-[#364153] hover:bg-[#4a5565] transition duration-200 font-bold py-2 px-4 rounded border-0 cursor-pointer text-white!\\' type=\\'submit\\'>Close</button></form></div></div></dialog>')"><svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="h-4 w-4 text-[#99a1af] hover:text-[#00d3f3] duration-200 transition-all">${icons.tableCopyHTML}</svg></div>
  `,
  layout: {
    type: 'table',
    columns: [
      { name: '', class: 'max-w-16' },
      ['Name'],
      { name: 'Requests', class: 'hidden md:block max-w-54' },
      { name: 'Variables', class: 'hidden lg:block' },
      { name: 'Response', class: 'hidden sm:block' },
      { name: 'Query', class: 'hidden md:block' },
    ],
    searchColumnsIndex: [1, 3, 4, 5],
    // @ts-expect-error Fix TypeScript type
    render: (request: {
      name: string
      query: string | null
      operationType?: 'query' | 'mutation' | 'subscription'
      variables: Record<string, unknown>
      response: Record<string, unknown>
      httpAnalyzer: { total?: number; hits?: number; miss?: number; duration: number }
      isError?: boolean
      source?: 'gql' | 'trpc'
      apiUrl?: string
    }) => {
      const responseSize = JSON.stringify(request.response).length / 1000 // value in kB
      const duration = request.httpAnalyzer.duration / 1000 // value in seconds
      const cacheHits =
        ((request.httpAnalyzer?.hits ?? 0) / (request.httpAnalyzer.total || 1)) * 100
      const responseSizeLabel =
        // 100 kB
        responseSize > 100
          ? // 1 000 kB (1 MB)
            `${responseSize > 1_000 ? "<span title='Request larger than 1 MB'>‼️ </span> " : "<span title='Request larger than 100 kB'>⚠️ </span>"}<span class='text-xs text-[#99a1af]'>${(responseSize).toFixed(0)} kB </span>`
          : ''
      // Duration 5 seconds and 2 seconds
      const durationLabel =
        duration >= 5
          ? "<span title='Request took more than 5 seconds to finish'>‼️ </span> "
          : duration > 2
            ? "<span title='Request took more than 2 seconds to finish'>⚠️ </span>"
            : ''

      const errorLabel = request.isError ? "<span title='Query contains error'>🚨 </span>" : ''

      const isTrpc = (request.source ?? 'gql') === 'trpc'

      const isMutation = isTrpc
        ? request.operationType === 'mutation'
        : (request.query ?? '').startsWith('mutation')

      const sourceBadge = `<span class="inline-block px-1 rounded text-[#364153] uppercase text-[10px] leading-4 ml-1.5 ${isTrpc ? 'bg-[#bfdbfe]' : 'bg-[#d1d5db]'}" title="${isTrpc ? 'tRPC' : 'GraphQL'}">${isTrpc ? 'tRPC' : 'GQL'}</span>`

      if (request.isError) {
        helpers.incrementNotificationBadge(requests.id)
      }

      return helpers.updateTable(requests.id, 'append', {
        rows: [
          {
            dataAttributes: {
              'data-query': request.query ?? '',
              'data-variables': request.variables,
              'data-request-name': request.name,
              'data-response': request.response,
              'data-error-label': errorLabel,
              'data-response-size-label': responseSizeLabel,
              'data-duration': duration.toFixed(2),
              'data-duration-label': durationLabel,
              'data-source': request.source ?? 'gql',
              'data-trpc-url': request.apiUrl ?? '',
              'data-operation-type': request.operationType ?? '',
            },
            data: [
              {
                render: () => {
                  if (isTrpc) {
                    return `
                      <span class="inline-flex p-0 max-w-8 w-full items-center justify-center opacity-20 cursor-not-allowed" title="Playground not available for tRPC">
                        <svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-3 h-3">${icons.playgroundCopy}</svg>
                      </span>
                      <button
                        fu-tools-${requests.id}-copy-as-curl
                        title="Copy as cURL"
                        class="focus:scale-120 cursor-pointer bg-transparent border-0 p-0 max-w-8 w-full whitespace-nowrap text-sm text-white! overflow-hidden text-ellipsis transition duration-200 hover:text-[#00d3f3]!">
                        <svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-3 h-3">${icons.fetchCopy}</svg>
                      </button>
                    `
                  }
                  return `
                    <button fu-tools-${requests.id}-open-graphql-playground title="Open in GraphQL Playground" class="focus:scale-120 cursor-pointer bg-transparent border-0 p-0 max-w-8 w-full whitespace-nowrap text-sm text-white! overflow-hidden text-ellipsis transition duration-200 hover:text-[#00d3f3]!"><svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-3 h-3">${icons.playgroundCopy}</svg></button>
                    <button
                      fu-tools-${requests.id}-copy-as-curl
                      title="Copy as cURL"
                      class="focus:scale-120 cursor-pointer bg-transparent border-0 p-0 max-w-8 w-full whitespace-nowrap text-sm text-white! overflow-hidden text-ellipsis transition duration-200 hover:text-[#00d3f3]!">
                      <svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-3 h-3">${icons.fetchCopy}</svg>
                    </button>
                  `
                },
              },
              {
                render: () =>
                  helpers.createHTMLElement({
                    className:
                      'px-4 py-2 w-full whitespace-nowrap text-sm text-white! overflow-hidden text-ellipsis',
                    title: request.name,
                    content: `<!--import--><div class="inline-block w-4 h-4 text-[#364153] rounded-full mr-2 uppercase text-xs/[17px] text-center ${isMutation ? 'bg-[#ddd6ff]' : 'bg-[#fff085]'}" title="${isMutation ? 'Mutation' : 'Query'}">${isMutation ? 'm' : 'q'}</div>${sourceBadge} ${errorLabel}${responseSizeLabel}${request.name}`,
                    // TODO: GET RID OF DANGEROUSLY SETTING INNER HTML (update content to use helpers.createHTMLElement)
                    dangerouslySetInnerHTML: true,
                  }).outerHTML,
              },
              {
                render: () =>
                  helpers.createHTMLElement({
                    className:
                      'hidden md:block px-4 py-2 max-w-54 w-full whitespace-nowrap text-sm text-white! overflow-hidden text-ellipsis',
                    title: isTrpc
                      ? ''
                      : `Request duration ${durationLabel}${(duration).toFixed(2)}s`,
                    dangerouslySetInnerHTML: isTrpc,
                    content: isTrpc
                      ? `- <span title="Due to tRPC batching, multiple requests may be batched into one HTTP request. This makes the timing unreliable — see the browser's Network tab for precise timings." class="text-[#6b7280] cursor-help">ⓘ</span>`
                      : () =>
                          helpers.createHTMLElements([
                            {
                              title: `Request duration ${durationLabel}${(duration).toFixed(2)}s`,
                              content: `${durationLabel}${(duration).toFixed(2)}s`,
                              element: 'span',
                              // TODO: GET RID OF DANGEROUSLY SETTING INNER HTML (update content to use helpers.createHTMLElement)
                              dangerouslySetInnerHTML: true,
                            },
                            ...(request.httpAnalyzer?.total
                              ? [
                                  {
                                    className: '',
                                    element: 'span',
                                    content: request.httpAnalyzer?.total
                                      ? () =>
                                          helpers.createHTMLElements([
                                            {
                                              className: '',
                                              element: 'span',
                                              content: ` · `,
                                            },
                                            {
                                              className: '',
                                              element: 'span',
                                              title: `Requests to database ${request.httpAnalyzer?.total}`,
                                              content: `${request.httpAnalyzer?.total}`,
                                            },
                                          ])
                                      : '',
                                  },
                                  {
                                    className: 'text-xs text-[#99a1af]',
                                    title: `Cache hit percentage ${cacheHits.toFixed(2)} %`,
                                    element: 'span',
                                    content: ` ${cacheHits.toFixed(2)} %`,
                                  },
                                ]
                              : []),
                          ]),
                  }).outerHTML,
              },
              {
                render: () =>
                  helpers.createHTMLElement({
                    className:
                      'hidden md:block px-4 py-2 w-full whitespace-nowrap text-sm text-white! overflow-hidden text-ellipsis',
                    title: helpers.convertObjectToString(request.variables),
                    content: helpers.convertObjectToString(request.variables),
                  }).outerHTML,
              },
              {
                render: () =>
                  helpers.createHTMLElement({
                    className:
                      'hidden md:block px-4 py-2 w-full whitespace-nowrap text-sm text-white! overflow-hidden text-ellipsis',
                    title: helpers.convertObjectToString(request.response),
                    content: helpers.convertObjectToString(request.response),
                  }).outerHTML,
              },
              {
                render: () =>
                  helpers.createHTMLElement({
                    className:
                      'hidden md:block px-4 py-2 w-full whitespace-nowrap text-sm text-white! overflow-hidden text-ellipsis',
                    title: request.query ?? '-',
                    content: request.query ?? '-',
                  }).outerHTML,
              },
            ],
          },
        ],
        isRowClickable: true,
      })
    },
  },
  render: (...data: [unknown, ...unknown[]]) => requests.layout.render(...data),
  init: () => {
    document
      .querySelector(`[data-fu-tools-table-content=${requests.id}]`)
      ?.addEventListener('click', event => {
        const tableRow = (event.target as HTMLElement).closest('[fu-tools-table-row]')
        if (!tableRow) {
          return
        }

        const query = tableRow.getAttribute('data-query') ?? ''
        const variables = tableRow.getAttribute('data-variables') ?? '{}'
        const response = tableRow.getAttribute('data-response') ?? '{}'
        const errorLabel = tableRow.getAttribute('data-error-label') ?? ''
        const responseSizeLabel = tableRow.getAttribute('data-response-size-label') ?? ''
        const duration = Number(tableRow.getAttribute('data-duration') ?? 0)
        const durationLabel = tableRow.getAttribute('data-duration-label') ?? ''
        const requestName = tableRow.getAttribute('data-request-name') ?? ''
        const source = tableRow.getAttribute('data-source') ?? 'gql'
        const trpcUrl = tableRow.getAttribute('data-trpc-url') ?? ''
        const operationType = tableRow.getAttribute('data-operation-type') ?? ''

        const isOpenPlaygroundButton = (event.target as HTMLElement).closest(
          `[fu-tools-${requests.id}-open-graphql-playground]`
        )
        const isCopyAsCurlButton = (event.target as HTMLElement).closest(
          `[fu-tools-${requests.id}-copy-as-curl]`
        )

        if (isCopyAsCurlButton) {
          const token = sessionStorage.getItem('jwt') ?? localStorage.getItem('jwt') ?? ''
          if (source === 'trpc') {
            const input = JSON.parse(variables)
            const curlCmd =
              operationType === 'mutation'
                ? `curl '${trpcUrl}?batch=1' -X POST -H 'content-type: application/json' -H 'authorization: Bearer ${token}' -d '${JSON.stringify({ 0: input })}'`
                : `curl '${trpcUrl}?batch=1&input=${encodeURIComponent(JSON.stringify({ 0: input }))}' -H 'authorization: Bearer ${token}'`
            navigator.clipboard.writeText(curlCmd)
          } else {
            const tenant =
              (window.location.pathname.split('/')[1] ?? '').split('-').length === 5
                ? ` -H 'x-tenant: ${window.location.pathname.split('/')[1]}'`
                : ''
            const language = localStorage.getItem('LANGUAGE') ?? 'En_Us'
            navigator.clipboard.writeText(
              `curl '${configuration.apiUrl}' -X POST -H 'content-type: application/json' -H 'authorization: Bearer ${token}' -H 'language: ${language}'${tenant} -d '${JSON.stringify({ variables: JSON.parse(variables), query })}'`
            )
          }
          return
        }

        if (isOpenPlaygroundButton) {
          const payload = {
            name: 'SetOperation',
            operation: query,
            variables,
          }

          const iframe = document.querySelector('#fu-tools-playground-sandbox iframe')
          if (iframe instanceof HTMLIFrameElement) {
            iframe.contentWindow?.postMessage(payload, '*')
          } else {
            const sandbox = document.querySelector('#fu-tools-playground-sandbox')
            sandbox?.setAttribute('fu-tools-playground-init-state', JSON.stringify(payload))
          }
          const menuElement = document.querySelector(
            '[data-fu-tools-menu=playground]'
          ) as HTMLElement
          menuElement?.click()

          return
        }

        const title = helpers.createHTMLElement({
          className: 'flex font-bold border-[#4a5565] border-b pb-4',
          content: `${errorLabel}${responseSizeLabel}<span class='text-xs text-[#99a1af]'>${durationLabel}${duration}s </span>${requestName}`,
          // TODO: GET RID OF DANGEROUSLY SETTING INNER HTML (update content to use helpers.createHTMLElement)
          dangerouslySetInnerHTML: true,
        })
        const makeLabel = (text: string) =>
          helpers.createHTMLElement({
            className: 'pt-4 pb-1 text-xs font-semibold uppercase tracking-wider text-[#99a1af]',
            content: text,
          })

        const content = helpers.createHTMLElement({
          className:
            'border-[#4a5565] border-b pb-4 cursor-pointer hover:bg-[#364153] transition-all duration-200',
          content: helpers.convertObjectToSanitizedDialogHTML(JSON.parse(variables)),
          dangerouslySetInnerHTML: true,
          copyOnClick: true,
        })
        const content2 = helpers.createHTMLElement({
          className:
            'border-[#4a5565] border-b pb-4 cursor-pointer hover:bg-[#364153] transition-all duration-200',
          content: helpers.convertObjectToSanitizedDialogHTML(JSON.parse(response)),
          dangerouslySetInnerHTML: true,
          copyOnClick: true,
        })
        const content3 =
          source === 'trpc'
            ? helpers.createHTMLElement({
                className: 'border-[#4a5565] border-b pb-4',
                content: '-',
              })
            : helpers.createHTMLElement({
                className:
                  'border-[#4a5565] border-b pb-4 cursor-pointer hover:bg-[#364153] transition-all duration-200',
                content: helpers.replaceNewlinesToHTML(syntaxHighlighter.graphql(query)),
                dangerouslySetInnerHTML: true,
                copyOnClick: true,
              })

        const createdElements = document.createDocumentFragment()
        createdElements.append(
          title,
          makeLabel('Variables'),
          content,
          makeLabel('Response'),
          content2,
          makeLabel('Query'),
          content3
        )

        helpers.showModal(createdElements)
      })
  },
}
